import { type VoidFunctionComponent } from 'react';

import type { LicensingRule } from '../../../types';
import { Checkbox } from '../../generic';
import Icon, { icons } from '../../generic/Icon';
import Tooltip from '../../generic/Tooltip';
import styles from './Rules.module.less';
import type { LicensingRuleType } from '../../../../shared/model-typings';
import List from '../../generic/List';
import Flex from '../../generic/Flex';
import TooltipIcon from '../../generic/TooltipIcon';
import { TooltipButton } from '../../generic/Button';

type RuleItemProps = {
  rule: LicensingRule;
  isChecked: boolean;
  ruleType?: LicensingRuleType;
  onCheck: (isChecked: boolean, licenseId: string, ruleType?: LicensingRuleType) => void;
  onLicenseClick: (licenseId: string | null, ruleId: number | null) => void;
};

//  Returns the text for the tooltip based on the specified depth condition.
function getDepthTooltip(depthCondition?: 'DIRECT' | 'DEEP'): string {
  if (depthCondition === 'DIRECT') {
    return 'found in your code';
  }
  if (depthCondition === 'DEEP') {
    return 'found in dependency';
  }
  return '';
}

function getNameConditionTooltip(nameCondition?: string): string {
  return nameCondition
    ? `name contains ${nameCondition}`
    : '';
}

function getLinkingTooltip(linkingCondition?: 'STATIC' | 'DYNAMIC'): string {
  if (linkingCondition === 'STATIC') {
    return 'statically linked';
  }
  if (linkingCondition === 'DYNAMIC') {
    return 'dynamically linked';
  }
  return '';
}

//  Build the condition tooltip.
function getConditionalTooltip(
  depthCondition?: 'DIRECT' | 'DEEP',
  nameCondition?: string,
  linkingCondition?: 'STATIC' | 'DYNAMIC',
): string {
  return [
    getDepthTooltip(depthCondition),
    getNameConditionTooltip(nameCondition),
    getLinkingTooltip(linkingCondition),
  ]
    .filter(value => !!value)
    .join(' & ');
}

export const RuleItem: VoidFunctionComponent<RuleItemProps> = (props) => {
  const { depthCondition, nameCondition, linkingCondition } = props.rule;

  const hasCondition = !!(depthCondition || nameCondition || linkingCondition);

  const conditionalTooltip = hasCondition
    ? `If ${getConditionalTooltip(depthCondition ?? undefined, nameCondition ?? undefined, linkingCondition ?? undefined)}.`
    : '';

  const checkbox = (
    <Checkbox
      isChecked={props.isChecked}
      onChange={(_, isChecked) => props.onCheck(isChecked, props.rule.licenseId, props.ruleType)}
    />
  );

  const viewLicenseButton = (
    <div className={styles.viewLicenseButton}>
      <TooltipButton
        id={`rule-item-view-license-${props.rule.licenseId.toLowerCase()}`}
        tooltip="View License"
        size="micro"
        type="outline"
        icon={icons.eye}
        onClick={() => props.onLicenseClick(props.rule.licenseId ?? null, props.rule.ruleId ?? null)}
      />
    </div>
  );

  const content = (
    <Flex gap={8}>
      <div className={styles.ruleItemTitle}>
        {props.rule.licenseId}
      </div>
      {hasCondition && (
        <Tooltip
          id={`rule-item-tooltip-${props.rule.licenseId.toLowerCase()}`}
          tooltip={conditionalTooltip}
          showTooltip={hasCondition}
        >
          <span className="policy-rule-condition margin-left-xs">
            <Icon icon={icons.questionCircle} />
          </span>
        </Tooltip>
      )}
      {props.rule.notes && (
        <TooltipIcon
          id={`rule-item-notes-${props.rule.licenseId.toLowerCase()}`}
          icon={icons.noteRegular}
          tooltip={props.rule.notes}
        />
      )}
    </Flex>
  );

  return (
    <List.RowContainer>
      <List.Row left={checkbox} right={viewLicenseButton}>
        {content}
      </List.Row>
    </List.RowContainer>
  );
};
